/* Plans — the catalogue every grant points at.
 *
 * Not a tenant page: a plan belongs to the product rather than to a customer,
 * so like Admins it does not register with nav.js and is reached from the top
 * bar instead.
 *
 * ── THE HAZARD, STATED ONCE HERE AND AGAIN ON THE SCREEN ────────────────────
 *
 * Every other screen in this app changes one salon. This one changes all of
 * them at once: a plan's features are resolved live — `private.license_resolve`
 * unions them with the grant's every time anybody asks — so taking `channel`
 * out of Standard switches the mailbox off for every salon on Standard, on
 * their next page load, with no grant having been made or withdrawn and nothing
 * in the audit log against any of them.
 *
 * That is the correct behaviour and it is why a plan is a plan. The design
 * decision this file makes is not to hide it: each row says how many salons are
 * on it, the feature toggles are confirmed rather than instant, and the count
 * is in the confirmation text. A screen that made this feel like editing a
 * label would be the most dangerous screen in the product.
 *
 * The alternative — freezing the features onto every grant when it is made —
 * was considered and rejected: it would mean a plan could never be corrected,
 * and every salon would drift into holding its own private version of Standard.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { store } from '../store.js';
import { licensing } from '../rules/licensing.js';
import { ConfirmButton } from '../components/confirm-button.js';

// A plan id is written into every grant that ever points at it and cannot be
// changed afterwards without rewriting history, so it is constrained to the
// shape the seeded ones have rather than to whatever was typed.
function cleanId(value) {
  return String(value || '').trim().toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32);
}

export const Plans = {
  name: 'AdminPlans',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var creating = Vue.ref(false);
    var draft = Vue.reactive({ id: '', name: '', blurb: '', seats: '', features: {} });

    Vue.onMounted(function () {
      store.loadPlans(true);
      if (!store.listLoaded.value) store.loadTenants();
    });

    // How many salons each plan is actually resolving to right now — the one in
    // force, not every grant that ever named it. It comes off the tenant list,
    // which already carries the reading; asking per plan would be a query per
    // row to answer a number that is already on screen.
    var holders = Vue.computed(function () {
      var out = {};
      store.tenants.value.forEach(function (t) {
        if (!t.license_plan_id) return;
        if (!out[t.license_plan_id]) out[t.license_plan_id] = { active: 0, lapsed: 0 };
        if (t.license_state === 'lapsed') out[t.license_plan_id].lapsed += 1;
        else out[t.license_plan_id].active += 1;
      });
      return out;
    });

    function held(id) {
      return holders.value[id] || { active: 0, lapsed: 0 };
    }

    var rows = Vue.computed(function () {
      return store.plans.value.map(function (p) {
        var count = held(p.id);
        return {
          plan: p,
          features: licensing.normalise(p.features),
          active: count.active,
          lapsed: count.lapsed,
          // What the confirmation on a feature toggle has to say. Only salons
          // with a LIVE licence on this plan feel a change today; a lapsed one
          // is entitled to nothing either way.
          reach: count.active === 0
            ? 'No salon is on this plan today'
            : count.active + (count.active === 1 ? ' salon is on this plan' : ' salons are on this plan')
        };
      });
    });

    // Every feature this build knows, plus any a plan already carries that it
    // does not — the same rule the grant form applies, for the same reason: an
    // admin app one release behind its console must not silently drop an id.
    var featureRows = Vue.computed(function () {
      var seen = {};
      var out = licensing.FEATURES.map(function (f) {
        seen[f.id] = true;
        return { id: f.id, label: f.label, hint: f.hint, known: true };
      });
      store.plans.value.forEach(function (p) {
        (p.features || []).forEach(function (id) {
          if (seen[id] || !id) return;
          seen[id] = true;
          out.push({
            id: id, label: id, known: false,
            hint: 'This build of the admin app does not know this feature. It is kept exactly as it is.'
          });
        });
      });
      return out;
    });

    function has(plan, id) { return (plan.features || []).indexOf(id) !== -1; }

    async function toggleFeature(plan, id) {
      var next = has(plan, id)
        ? (plan.features || []).filter(function (f) { return f !== id; })
        : (plan.features || []).concat([id]);
      await store.savePlan(plan.id, { features: licensing.normalise(next) });
    }

    async function saveField(plan, field, value) {
      var patch = {};
      if (field === 'seats') {
        var seats = licensing.cleanSeats(value);
        if (seats === undefined) {
          api.toast('A plan needs at least one seat, or none at all for no limit.', 'bad');
          return;
        }
        patch.seats = seats;
      } else {
        var text = String(value == null ? '' : value).trim();
        if (text === (plan[field] || '')) return;
        patch[field] = text || null;
      }
      await store.savePlan(plan.id, patch);
    }

    async function retire(plan, on) {
      await store.savePlan(plan.id, { retired: on });
      api.toast(on
        ? 'Retired. Every salon holding it keeps it; it is simply not offered for a new grant.'
        : 'Back in the list. It can be granted again.');
    }

    async function makeDefault(plan) {
      if (await store.setDefaultPlan(plan.id)) {
        api.toast('New tenants are granted ' + plan.name + ' when they are created.');
      }
    }

    async function create() {
      var id = cleanId(draft.id || draft.name);
      if (!id) { api.toast('A plan needs an id.', 'bad'); return; }
      if (store.plans.value.some(function (p) { return p.id === id; })) {
        api.toast('There is already a plan with that id.', 'bad');
        return;
      }
      var seats = licensing.cleanSeats(draft.seats);
      if (seats === undefined) {
        api.toast('A plan needs at least one seat, or none at all for no limit.', 'bad');
        return;
      }
      var okay = await store.createPlan({
        id: id,
        name: draft.name.trim() || id,
        blurb: draft.blurb.trim() || null,
        seats: seats,
        features: licensing.normalise(Object.keys(draft.features).filter(function (k) {
          return draft.features[k];
        })),
        sort_order: (store.plans.value.length + 1) * 10
      });
      if (!okay) return;
      draft.id = '';
      draft.name = '';
      draft.blurb = '';
      draft.seats = '';
      draft.features = {};
      creating.value = false;
    }

    function toggleDraft(id) {
      var next = Object.assign({}, draft.features);
      if (next[id]) delete next[id]; else next[id] = true;
      draft.features = next;
    }

    return {
      rows: rows,
      loaded: store.plansLoaded,
      featureRows: featureRows,
      has: has,
      toggleFeature: toggleFeature,
      saveField: saveField,
      retire: retire,
      makeDefault: makeDefault,
      creating: creating,
      draft: draft,
      toggleDraft: toggleDraft,
      create: create,
      cleanId: cleanId
    };
  },
  template: [
    '<div class="screen">',
    '  <div class="screen__head">',
    '    <div>',
    '      <h2>Plans</h2>',
    '      <p class="screen__lead">What a licence can be a licence <em>for</em>. A plan is a name',
    '        and a set of features; a grant points at one and may add to it. Editing a plan',
    '        changes every salon holding it, at once, without a grant being made.</p>',
    '    </div>',
    '    <div class="screen__actions">',
    '      <button type="button" class="btn btn--primary" @click="creating = !creating">',
    '        {{ creating ? "Cancel" : "New plan" }}</button>',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card card--warn">',
    '    <h3>What editing one of these does</h3>',
    '    <p class="hint">A plan&rsquo;s features are resolved every time anybody asks, so taking',
    '      one out takes it out of every console on that plan by their next page load &mdash; no',
    '      grant is made, no grant is withdrawn, and there is nothing in any salon&rsquo;s',
    '      activity to explain it. That is what makes a plan a plan, and it is why the toggles',
    '      below ask twice and say how many salons they are about to reach.</p>',
    '    <p class="hint">To change ONE salon, do not touch this screen: grant that salon the',
    '      feature as an extra on its own Licence page, or move it to a different plan.</p>',
    '  </div>',
    '',
    '  <form v-if="creating" class="card" @submit.prevent="create">',
    '    <h3>New plan</h3>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Name</span>',
    '        <input v-model="draft.name" class="input" type="text" maxlength="60"',
    '          placeholder="What it is called on an invoice" />',
    '      </label>',
    '      <label class="field">',
    '        <span class="field__label">Id</span>',
    '        <input v-model="draft.id" class="input input--key mono" type="text" maxlength="32"',
    '          :placeholder="cleanId(draft.name) || \'standard\'" />',
    '      </label>',
    '      <label class="field">',
    '        <span class="field__label">Seats</span>',
    '        <input v-model="draft.seats" class="input input--mins" type="number" min="1" max="999"',
    '          placeholder="no limit" />',
    '      </label>',
    '    </div>',
    '    <label class="field field--grow">',
    '      <span class="field__label">Blurb</span>',
    '      <input v-model="draft.blurb" class="input" type="text" maxlength="160"',
    '        placeholder="One line, for whoever is choosing between them" />',
    '    </label>',
    '    <div class="chips">',
    '      <button v-for="f in featureRows" :key="f.id" type="button" class="chip"',
    '        :class="{ \'chip--on\': draft.features[f.id] }" @click="toggleDraft(f.id)">',
    '        {{ f.label }}</button>',
    '    </div>',
    '    <p class="hint">The id is written into every grant that points at this plan and cannot',
    '      be changed afterwards without rewriting them, so it is fixed at creation.</p>',
    '    <div class="card__row">',
    '      <button type="submit" class="btn btn--primary">Create</button>',
    '    </div>',
    '  </form>',
    '',
    '  <p v-if="!loaded" class="empty">Loading plans…</p>',
    '  <p v-else-if="!rows.length" class="empty">No plans. Run',
    '    <code>admin/server/schema/14-licensing.sql</code> — it seeds three.</p>',
    '',
    '  <ul v-else class="rows">',
    '    <li v-for="r in rows" :key="r.plan.id" class="row row--stack"',
    '      :class="{ \'is-muted\': r.plan.retired }">',
    '      <div class="row__head">',
    '        <span class="row__name">{{ r.plan.name }}</span>',
    '        <span class="row__key mono">{{ r.plan.id }}</span>',
    '        <span v-if="r.plan.is_default" class="tag tag--set">default for new tenants</span>',
    '        <span v-if="r.plan.retired" class="tag">retired</span>',
    '        <span class="row__end">',
    '          <span class="pill" :class="r.active ? \'pill--ok\' : \'\'">{{ r.active }} live</span>',
    '          <span v-if="r.lapsed" class="pill pill--bad">{{ r.lapsed }} lapsed</span>',
    '        </span>',
    '      </div>',
    '',
    '      <div class="row__body">',
    '        <div class="card__row">',
    '          <label class="field field--grow">',
    '            <span class="field__label">Name</span>',
    '            <input class="input" type="text" maxlength="60" :value="r.plan.name"',
    '              @change="saveField(r.plan, \'name\', $event.target.value)" />',
    '          </label>',
    '          <label class="field field--grow">',
    '            <span class="field__label">Blurb</span>',
    '            <input class="input" type="text" maxlength="160" :value="r.plan.blurb"',
    '              @change="saveField(r.plan, \'blurb\', $event.target.value)" />',
    '          </label>',
    '          <label class="field">',
    '            <span class="field__label">Seats</span>',
    '            <input class="input input--mins" type="number" min="1" max="999"',
    '              :value="r.plan.seats == null ? \'\' : r.plan.seats" placeholder="no limit"',
    '              @change="saveField(r.plan, \'seats\', $event.target.value)" />',
    '          </label>',
    '        </div>',
    '        <p class="hint">An empty seat box is no limit. A grant may override it for one',
    '          salon; this is what every other salon on the plan gets.</p>',
    '',
    '        <ul class="rows rows--tight">',
    '          <li v-for="f in featureRows" :key="f.id" class="row row--slim">',
    '            <span class="row__main">',
    '              <span class="row__name">{{ f.label }}',
    '                <span v-if="!f.known" class="tag tag--bad">unknown to this build</span></span>',
    '              <span class="row__desc">{{ f.hint }}</span>',
    '            </span>',
    '            <span class="row__key mono">{{ f.id }}</span>',
    '            <confirm-button :label="has(r.plan, f.id) ? \'Remove\' : \'Add\'" size="sm"',
    '              :kind="has(r.plan, f.id) ? \'danger\' : \'quiet\'"',
    '              :confirm="(has(r.plan, f.id) ? \'Remove from \' : \'Add to \') + r.plan.name + \' — \' + r.reach"',
    '              @confirmed="toggleFeature(r.plan, f.id)" />',
    '          </li>',
    '        </ul>',
    '',
    '        <div class="card__row">',
    '          <button v-if="!r.plan.is_default && !r.plan.retired" type="button"',
    '            class="btn btn--quiet btn--sm" @click="makeDefault(r.plan)">',
    '            Make the default</button>',
    '          <confirm-button v-if="!r.plan.retired && !r.plan.is_default" label="Retire" size="sm"',
    '            kind="quiet" confirm="Stop offering it — salons holding it keep it"',
    '            @confirmed="retire(r.plan, true)" />',
    '          <button v-if="r.plan.retired" type="button" class="btn btn--quiet btn--sm"',
    '            @click="retire(r.plan, false)">Offer it again</button>',
    '          <span v-if="r.plan.is_default" class="hint hint--inline">The default plan cannot be',
    '            retired &mdash; something has to be granted to a tenant the moment it is created.</span>',
    '        </div>',
    '      </div>',
    '    </li>',
    '  </ul>',
    '',
    '  <div class="card">',
    '    <h3>Adding a feature</h3>',
    '    <p class="hint">A feature exists because a build of the <strong>console</strong> looks',
    '      for its id &mdash; there is no table of them and no foreign key, deliberately, so the',
    '      database never holds a staler opinion than the code. Adding one is a console release',
    '      that checks for it, then adding the id to a plan here. The ids this build knows are',
    '      listed in <code>admin/js/rules/licensing.js</code>; anything else is shown marked and',
    '      granted as it stands.</p>',
    '  </div>',
    '</div>'
  ].join('\n')
};
